import { useEffect, useState } from "react";
import {
  applyLeave,
  getMyLeaves,
  getLeaveTypes,
} from "../../api/leaveApi";
import Loader from "../../components/Loader";
import { getMyProfile } from "../../api/employeeApi";
import { formatDateDMY } from "../../utils/dateFormatter";

/* ─── Premium Confirm Modal ─── */
const ConfirmModal = ({ open, onClose, onConfirm, form, leaveTypes, endDate }) => {
  if (!open) return null;
  const selectedType = leaveTypes.find((lt) => String(lt.id) === String(form.leave_type));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 px-8 pt-8 pb-6 text-center border-b border-blue-100">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-blue-500/10 flex items-center justify-center">
            <span className="text-3xl">📅</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Submit Leave Request</h3>
          <p className="text-sm text-slate-500 mt-1">Please review your leave details before submitting.</p>
        </div>

        {/* Details */}
        <div className="px-8 py-6 space-y-3">
          <DetailRow label="Leave Type" value={selectedType?.name || "—"} />
          <DetailRow label="From" value={form.start_date ? formatDateDMY(form.start_date) : "—"} />
          <DetailRow label="To" value={endDate ? formatDateDMY(endDate) : "—"} />
          <DetailRow label="Days" value={form.is_half_day === "HALF" ? "0.5 (Half Day)" : `${form.leave_days} day(s)`} />
          {form.reason && <DetailRow label="Reason" value={form.reason} />}
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold shadow-lg shadow-blue-200 hover:shadow-blue-300 hover:opacity-95 transition-all text-sm">
            Submit Request
          </button>
        </div>
      </div>
      <style>{`
        @keyframes modalIn {
          from { opacity:0; transform:scale(0.92) translateY(16px); }
          to   { opacity:1; transform:scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

/* ─── Success Modal ─── */
const SuccessModal = ({ open, onClose }) => {
  useEffect(() => {
    if (open) {
      const t = setTimeout(onClose, 4000);
      return () => clearTimeout(t);
    }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden text-center"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}
      >
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 px-8 pt-8 pb-6 border-b border-emerald-100">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <span className="text-4xl">✅</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Leave Request Submitted!</h3>
          <p className="text-sm text-emerald-700 mt-2 font-medium">Your leave request has been submitted successfully.</p>
          <p className="text-xs text-slate-400 mt-1">Waiting for manager approval.</p>
        </div>
        <div className="px-8 py-6">
          <button onClick={onClose}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold shadow-lg shadow-emerald-200 hover:opacity-95 transition-all text-sm">
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Error Modal ─── */
const ErrorModal = ({ open, message, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden text-center"
        style={{ animation: "modalIn 0.22s cubic-bezier(.22,.68,0,1.2)" }}
      >
        <div className="bg-gradient-to-br from-rose-50 to-red-50 px-8 pt-8 pb-6 border-b border-rose-100">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-rose-500/10 flex items-center justify-center">
            <span className="text-4xl">❌</span>
          </div>
          <h3 className="text-xl font-extrabold text-slate-800">Submission Failed</h3>
          <p className="text-sm text-rose-700 mt-2 font-medium">{message || "Failed to apply leave."}</p>
        </div>
        <div className="px-8 py-6">
          <button onClick={onClose}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-rose-500 to-red-600 text-white font-bold shadow-lg shadow-rose-200 hover:opacity-95 transition-all text-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Detail row helper ─── */
const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-slate-50 last:border-0">
    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{label}</span>
    <span className="text-sm font-semibold text-slate-700 text-right max-w-[60%]">{value}</span>
  </div>
);

const ApplyLeave = () => {
  // ================= STATE =================
  const [employee, setEmployee] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endDate, setEndDate] = useState("");

  const [showConfirm, setShowConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    leave_type: "",
    start_date: "",
    leave_days: 1,
    is_half_day: "FULL",
    reason: "",
  });

  // ================= LOAD DATA =================
  useEffect(() => {
    getMyProfile().then((res) => setEmployee(res.data));
    getLeaveTypes().then((res) => setLeaveTypes(res.data));
    loadLeaves();
  }, []);

  const loadLeaves = () => {
    setLoading(true);
    getMyLeaves()
      .then((res) => setLeaves(res.data))
      .finally(() => setLoading(false));
  };

  // ================= AUTO END DATE =================
  useEffect(() => {
    if (!form.start_date || !form.leave_days) {
      setEndDate("");
      return;
    }
    const start = new Date(form.start_date);
    const days = Math.max(Number(form.leave_days) - 1, 0);
    start.setDate(start.getDate() + days);
    setEndDate(start.toISOString().split("T")[0]);
  }, [form.start_date, form.leave_days]);

  // ================= SUBMIT =================
  const submitLeave = (e) => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const confirmSubmit = async () => {
    setShowConfirm(false);
    try {
      await applyLeave({
        leave_type: form.leave_type,
        start_date: form.start_date,
        leave_days: form.is_half_day === "HALF" ? 0.5 : form.leave_days,
        is_half_day: form.is_half_day,
        reason: form.reason,
      });
      setShowSuccess(true);
      setForm({ leave_type: "", start_date: "", leave_days: 1, is_half_day: "FULL", reason: "" });
      loadLeaves();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Failed to apply leave");
    }
  };

  if (!employee) return <Loader />;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Modals */}
      <ConfirmModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={confirmSubmit}
        form={form}
        leaveTypes={leaveTypes}
        endDate={endDate}
      />
      <SuccessModal open={showSuccess} onClose={() => setShowSuccess(false)} />
      <ErrorModal open={!!errorMsg} message={errorMsg} onClose={() => setErrorMsg("")} />

      <h2 className="text-2xl font-semibold mb-6">Leave Application</h2>

      {/* ================= EMPLOYEE DETAILS ================= */}
      <div className="bg-white p-6 rounded shadow mb-6">
        <h3 className="font-semibold mb-4 text-gray-700">Employee Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Employee Code</label>
            <input disabled className="input-readonly" value={employee.employee_code} />
          </div>
          <div>
            <label className="label">Name of Company</label>
            <input disabled className="input-readonly" value={employee.company_name} />
          </div>
          <div>
            <label className="label">Employee Name</label>
            <input disabled className="input-readonly" value={employee.full_name} />
          </div>
          <div>
            <label className="label">Employee Contact No</label>
            <input disabled className="input-readonly" value={employee.phone_number} />
          </div>
        </div>
      </div>

      {/* ================= APPLY LEAVE ================= */}
      <form onSubmit={submitLeave} className="bg-white p-6 rounded shadow mb-8">
        <h3 className="font-semibold mb-4 text-gray-700">Leave Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Leave Start Date *</label>
            <input type="date" className="input" value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </div>
          <div>
            <label className="label">Leave Days *</label>
            <input type="number" min="0.5" step="0.5" className="input"
              disabled={form.is_half_day === "HALF"} value={form.leave_days}
              onChange={(e) => setForm({ ...form, leave_days: e.target.value })} />
          </div>
          <div>
            <label className="label">Is Half Day *</label>
            <select className="input" value={form.is_half_day}
              onChange={(e) => setForm({ ...form, is_half_day: e.target.value })}>
              <option value="FULL">No</option>
              <option value="HALF">Yes</option>
            </select>
          </div>
          <div>
            <label className="label">Leave End Date</label>
            <input readOnly className="input-readonly" value={endDate ? formatDateDMY(endDate) : ""} />
          </div>
          <div>
            <label className="label">Leave Type *</label>
            <select className="input" value={form.leave_type}
              onChange={(e) => setForm({ ...form, leave_type: e.target.value })} required>
              <option value="">Select</option>
              {leaveTypes
                .filter((lt) => !lt.name.toLowerCase().includes("compensatory") && !lt.name.toLowerCase().includes("comp off"))
                .map((lt) => (<option key={lt.id} value={lt.id}>{lt.name}</option>))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <label className="label">Reason for Leave</label>
          <textarea rows="3" className="input" value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>
        <div className="mt-6 text-right">
          <button className="btn-primary">Apply Leave</button>
        </div>
      </form>

      {/* ================= LEAVE HISTORY ================= */}
      <div className="bg-white rounded-xl shadow">
        <h3 className="font-semibold p-4 border-b text-gray-700">Leave History</h3>
        {loading ? (
          <Loader />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-700">
              <thead className="bg-gray-100 text-xs uppercase tracking-wide text-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">From</th>
                  <th className="px-4 py-3 text-left">To</th>
                  <th className="px-4 py-3 text-center">Days</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-left">Remark</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {leaves.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{l.leave_type_name || l.leave_type}</td>
                    <td className="px-4 py-3">{formatDateDMY(l.start_date)}</td>
                    <td className="px-4 py-3">{formatDateDMY(l.end_date)}</td>
                    <td className="px-4 py-3 text-center">{l.leave_days}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center justify-center min-w-[90px] px-3 py-1 rounded-full text-xs font-semibold
                        ${l.status === "APPROVED" ? "bg-green-100 text-green-700"
                          : l.status === "REJECTED" ? "bg-red-100 text-red-700"
                          : "bg-yellow-100 text-yellow-700"}`}>
                        {l.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {l.status === "REJECTED" && l.rejection_reason
                        ? <p className="text-xs text-red-600 leading-relaxed">{l.rejection_reason}</p>
                        : <span className="text-gray-400 italic">—</span>}
                    </td>
                  </tr>
                ))}
                {leaves.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-4 py-6 text-center text-gray-500">No leave records</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplyLeave;
